import { Box, HStack, IconButton } from "@chakra-ui/react";
import { MdDragIndicator } from "react-icons/md";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CDS from "design-system";

import CaminoDuplicate from "components/Icon/CaminoDuplicate";
import CaminoTrash from "components/Icon/CaminoTrash";
import FieldRenderer from "../../Fields/FieldRenderer";
import { FIELD_TYPE_ICON, FIELD_TYPE_LABEL } from "../../constants";
import type { CustomFormFieldPayload } from "../../models/payloads";

interface Props {
  sortableId: string;
  field: CustomFormFieldPayload;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function FieldTypeChip({ field }: { field: CustomFormFieldPayload }) {
  const Icon = FIELD_TYPE_ICON[field.fieldType];
  return (
    <HStack spacing={1} color="content.tertiary" px={2} py={0.5} borderRadius="base" bg="bg.tertiary">
      <Box as={Icon} boxSize={3} />
      <CDS.Text12 color="content.tertiary" fontWeight="medium">
        {FIELD_TYPE_LABEL[field.fieldType]}
      </CDS.Text12>
    </HStack>
  );
}

export default function FieldCard({ sortableId, field, isSelected, onSelect, onDuplicate, onDelete }: Props) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transition, transform, isDragging } = useSortable({
    id: sortableId,
  });

  const style: React.CSSProperties = {
    transition,
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box ref={setNodeRef} style={style} role="group" onClick={onSelect} data-field-selected={isSelected}>
      <Box
        position="relative"
        bg="bg.secondary"
        borderRadius="base"
        borderWidth={isSelected ? "2px" : "1px"}
        borderColor={isSelected ? "cdsIndigo.500" : "border.default"}
        _hover={{ borderColor: isSelected ? "cdsIndigo.500" : "border.default" }}
        p={isSelected ? 3 : "13px"}
        cursor="pointer"
      >
        {/* Drag handle — a small tab centered on the top edge, appears on hover/selection. */}
        <Box
          position="absolute"
          top="-10px"
          left="50%"
          transform="translateX(-50%)"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.15s"
          onClick={stop}
        >
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            icon={<MdDragIndicator style={{ transform: "rotate(90deg)" }} />}
            variant="ghost"
            size="xs"
            cursor="grab"
            color="content.tertiary"
            bg="bg.secondary"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="base"
            _hover={{ color: "content.primary", bg: "bg.tertiary" }}
          />
        </Box>

        <HStack align="flex-start" spacing={3} justify="space-between">
          <Box flex={1} minW={0}>
            <FieldRenderer field={field} mode="preview" value={null} onChange={() => {}} />
          </Box>
          <HStack spacing={2} align="center" onClick={stop}>
            <FieldTypeChip field={field} />
            <HStack spacing={0}>
              <IconButton
                aria-label="Duplicate field"
                icon={<CaminoDuplicate color="inherit" boxSize={3.5} />}
                variant="ghost"
                size="sm"
                color="content.tertiary"
                _hover={{ color: "content.primary", bg: "bg.tertiary" }}
                onClick={onDuplicate}
              />
              <IconButton
                aria-label="Delete field"
                icon={<CaminoTrash color="inherit" boxSize={3.5} />}
                variant="ghost"
                size="sm"
                color="content.tertiary"
                _hover={{ color: "content.primary", bg: "bg.tertiary" }}
                onClick={onDelete}
              />
            </HStack>
          </HStack>
        </HStack>
      </Box>
    </Box>
  );
}
